/**
 * The arithmetic behind the two diagnostics on the HUD, kept pure so it can be
 * tested: the measured frame rate, and the promotion bar.
 *
 * It sits in `game/` beside `mission-hud.ts` for exactly the reason written
 * there — `ArenaScene` reads half of it, the components read the other half,
 * and nothing in `game/` may import from `ui/`. No Babylon, no DOM, and the one
 * clock it touches is handed in as an argument.
 *
 * **Nothing in this file may ever become an input to the simulation, the
 * prediction or the camera.** Read the smoothness invariants in `CLAUDE.md`
 * first: the camera bug that cost the most to find was a frame-time-dependent
 * value sitting between the tank and the camera, and a *frame rate* is the
 * purest form of that value there is. `FrameRateMeter` is therefore a counter
 * with one reader, the HUD payload, and one other, the dev probe. It returns a
 * number to look at. If anything ever multiplies by it, that is the bug.
 */

import {
  CTF_CAPTURES_TO_WIN,
  ROUNDS_TO_WIN,
  SPAWN_ZONE_GRACE_SECONDS,
  WAVE_COUNT,
  missionKindFor,
  type MatchMode,
  type MatchScore,
  type MissionSnapshot,
  type TeamId,
} from '@shared/protocol';
import type { RankProgress } from '@shared/tank-spec';

import { legOnScreen, waveOnScreen } from './mission-hud';

/**
 * Milliseconds of rendered frames averaged into one reading.
 *
 * Short enough that the number answers "is it smooth *now*", long enough that it
 * is readable rather than flickering: at 60 Hz this is ~30 frames a reading, and
 * two readings a second is about as fast as a two-digit number can change
 * without becoming noise. Averaged rather than instantaneous because the
 * reciprocal of one frame's delta swings by ±15 at a steady 60 Hz and nobody can
 * read it.
 */
export const FPS_WINDOW_MS = 500;

/**
 * A single frame gap past which the window is thrown away instead of averaged.
 *
 * A backgrounded tab, a debugger pause and a page the browser has stopped
 * animating all produce **one** enormous gap, and averaging it in reports a
 * frame rate near zero for a second after the player comes back — a wrong number
 * about a period nobody was watching. Dropped rather than clamped, because a
 * clamp would still be a made-up frame in the average.
 *
 * Four seconds rather than something tighter on purpose: the headless renderer in
 * `frontend/e2e/` is ANGLE-over-SwiftShader and genuinely runs at a few frames a
 * second, and those frames are real. A threshold that discarded them would make
 * the meter answer 0 on the one stack where a case might ask.
 */
const STALL_MS = 4000;

/**
 * Frames per second, measured off the frames that were actually rendered.
 *
 * **Deliberately not `engine.getFps()`.** Babylon measures its own figure in
 * `beginFrame`, which runs on every animation frame — including the ones
 * `ArenaScene`'s frame-rate cap skips before doing any work. With `__sowFps(30)`
 * set on a 60 Hz display the engine reports ~60 while thirty frames a second are
 * being thrown away, so the readout would be double what is on screen. This
 * counts the frames that reached `scene.render()` and nothing else.
 *
 * Fed `performance.now()` by the caller rather than reading a clock itself, so a
 * test can advance time by hand and so there is exactly one call to
 * `performance.now()` per frame in the render loop — the caller already has it.
 */
export class FrameRateMeter {
  /** Rendered frames counted into the window that is currently open. */
  private frames = 0;
  /** When the open window started, or 0 before the first sample. */
  private openedAt = 0;
  /** Timestamp of the previous sample, for the stall guard. */
  private lastAt = 0;
  private measured = 0;

  /**
   * Counts one rendered frame.
   *
   * The very first sample only opens a window: one timestamp is not an interval,
   * and counting it would put a frame into an average with no time behind it.
   */
  sample(nowMs: number): void {
    if (this.openedAt === 0) {
      this.openedAt = nowMs;
      this.lastAt = nowMs;
      return;
    }

    // One absurd gap is not a frame rate. Reopen the window here and count this
    // frame as its first, so the next reading is about the frames since the
    // player came back rather than about the time they were away.
    if (nowMs - this.lastAt > STALL_MS) {
      this.openedAt = nowMs;
      this.lastAt = nowMs;
      this.frames = 0;
      return;
    }

    this.lastAt = nowMs;
    this.frames += 1;

    const elapsed = nowMs - this.openedAt;
    if (elapsed < FPS_WINDOW_MS) return;

    this.measured = (this.frames * 1000) / elapsed;
    this.frames = 0;
    this.openedAt = nowMs;
  }

  /**
   * The last complete reading, or 0 before the first window has closed.
   *
   * Zero rather than a guess from the partial window: half a second of "—" while
   * an arena is still behind the loading screen is honest, and a figure
   * extrapolated from three frames is not.
   */
  get fps(): number {
    return this.measured;
  }

  /** Rounded, which is the only form the HUD and the probe want. */
  get rounded(): number {
    return Math.round(this.measured);
  }

  /**
   * Forgets everything. Called when the scene is handed a new arena, so a
   * reading taken in the previous sortie cannot be the first thing on the new
   * HUD.
   */
  reset(): void {
    this.frames = 0;
    this.openedAt = 0;
    this.lastAt = 0;
    this.measured = 0;
  }
}

/** The promotion bar, resolved into the things the panel draws. */
export interface PromotionView {
  rank: number;
  title: string;
  /** 0–1, and 1 at the top of the ladder where there is nothing left to climb. */
  fraction: number;
  xpIntoRank: number;
  /** Null at the top rank. */
  xpForNextRank: number | null;
  atTop: boolean;
  /**
   * 0–1 width of the segment this battle just earned, drawn stacked after
   * `fraction` — the brighter half of a two-tone bar. Clamped so the pair can
   * never overflow the bar, and 0 when nothing was passed in.
   */
  gainedFraction: number;
  /**
   * True when the earned XP fills what was left of the current rank. The
   * client cannot see past this rank's span — the curve is the server's — so
   * the panel says the promotion lands on return to the garage rather than
   * naming the next rank's progress.
   */
  promoted: boolean;
}

/**
 * Turns the rank the garage resolved at join into a bar.
 *
 * **It does no progression arithmetic and must never start.** The rank, its name
 * and both XP figures are all the garage's own answers, couriered on
 * `TankSpec.rankAtJoin`; the only sum here is one division to turn two numbers
 * into a bar width. `RANK_TITLES` and the geometric curve behind them live in
 * `backend/src/garage/economy.ts` and are not shipped to the browser, which is
 * the property worth protecting: a client that could compute a rank would be a
 * second opinion about a number that is defined as a pure function of a column
 * only the server reads.
 *
 * `null` for an absent progress, which is what a spec resolved from a garage
 * that could not be read carries. The panel then draws no promotion block at
 * all — better than a full bar at Recruit, which is a claim about somebody's
 * account rather than an admission that nothing was known.
 *
 * `gainedXp` is the battle's own base XP, off the `battleOver` tally — final
 * the moment it exists, because the payout clock stops at the deciding tick —
 * drawn as a second, brighter segment on the same bar. It is still no
 * progression arithmetic: one more division, clamped so the two segments
 * cannot overflow, and a boolean saying whether the sum fills the rank. What
 * the *next* rank's span is stays the server's business, which is why a filled
 * bar reads "promoted on return" rather than starting a second bar.
 */
export function promotionView(
  progress: RankProgress | undefined,
  gainedXp = 0,
): PromotionView | null {
  if (!progress) return null;

  const needed = progress.xpForNextRank;
  const atTop = needed === null;
  const into = Number.isFinite(progress.xpIntoRank) ? Math.max(0, progress.xpIntoRank) : 0;
  const gained = Number.isFinite(gainedXp) ? Math.max(0, gainedXp) : 0;

  // A top rank reads as complete rather than as empty: there is no next rung, so
  // 0% would be the wrong end of the bar. A non-positive requirement can only
  // come from a broken curve and answers 0 rather than dividing by it.
  const fraction =
    atTop ? 1 : needed !== null && needed > 0 ? Math.min(1, into / needed) : 0;
  const gainedFraction =
    atTop || needed === null || needed <= 0
      ? 0
      : Math.min(1 - fraction, gained / needed);

  return {
    rank: progress.rank,
    title: progress.title,
    fraction,
    xpIntoRank: into,
    xpForNextRank: needed,
    atTop,
    gainedFraction,
    promoted: !atTop && needed !== null && needed > 0 && into + gained >= needed,
  };
}

/** One line naming what the mode was actually counting, and how it ended. */
export interface ObjectiveTally {
  /** What the mode counts, in the player's words. */
  label: string;
  /** The figure, already written out. */
  value: string;
}

/**
 * What the battle was *for*, resolved per mode.
 *
 * **The modes end differently and a summary that read correctly for only one of
 * them would be worse than none**, which is the whole reason this is a function
 * with a test rather than four `v-if`s in a template. A team battle counts
 * rounds, a convoy run counts legs, a relay raid counts cores and a last stand
 * counts waves; kills and deaths are the same in all four and are not this.
 *
 * Three properties are deliberate.
 *
 * **A deathmatch answers `null`, and so does anything without an objective.** A
 * deathmatch ends now — its timer or its kill target — but what it was counting
 * is kills, and kills are the scoreboard's whole subject on the result screen,
 * so an objective row would restate the table above it. The tutorial's endless
 * deathmatch never sets `outcome` at all, which is why the summary cannot
 * appear over a lesson and cannot promise a learner a payout the drill yard
 * does not make — by construction rather than by a branch.
 *
 * **The mission figures come out of the same helpers the live strips use.**
 * `waveOnScreen` and `legOnScreen` already own the two off-by-ones here — a
 * `wave` that means "the one fought or awaited" and a `leg` that is a 0-based
 * waypoint index — so the number on the summary is by construction the number
 * the strip was showing on the tick it all ended. A second reading of those
 * fields would be a second chance to be one out.
 *
 * **A failure states where it stopped rather than how much was held.** "Fell on
 * wave 5 of 8" is checkable against what the player watched; "held 4 waves" is a
 * claim about a wave that was part fought, and getting it wrong by one is exactly
 * the sort of thing nobody notices for months.
 */
export function objectiveTally(view: {
  mode?: MatchMode;
  won?: boolean;
  mission?: MissionSnapshot;
  score?: MatchScore;
  localTeam?: TeamId;
}): ObjectiveTally | null {
  const kind = view.mode ? missionKindFor(view.mode) : null;

  if (kind === null) {
    // Rounds, and only in the mode that has them. `score` is absent for the whole
    // of a deathmatch, which is the same guard by a different route.
    if (view.mode !== 'team-battle' || !view.score || !view.localTeam) return null;
    return {
      label: 'Rounds won',
      value: `${view.score[view.localTeam]} of ${ROUNDS_TO_WIN}`,
    };
  }

  const mission = view.mission;
  // A mission whose snapshot has gone is a mission we cannot describe. Silent
  // rather than guessed — the banner above already says whether it was won.
  if (!mission) return null;

  if (mission.kind === 'escort') {
    return {
      label: 'Convoy',
      value: view.won
        ? `delivered · ${mission.legs} legs`
        : `lost on leg ${legOnScreen(mission.leg, mission.legs)} of ${mission.legs}`,
    };
  }

  if (mission.kind === 'ctf') {
    return {
      label: 'Cores taken',
      value: `${mission.captures} of ${CTF_CAPTURES_TO_WIN}`,
    };
  }

  if (mission.kind === 'tutorial') {
    // Drills, counted the way every other mission's objective is counted: where
    // it stopped, not how much was held. The tutorial has no losing condition —
    // a destroyed learner is put back on their feet — so in practice this always
    // reads as the full seven; it says *which drill* anyway, because "all 7"
    // beside a summary that could only ever say that is a line nobody reads.
    return {
      label: 'Drills',
      value: view.won
        ? `all ${mission.steps} passed`
        : `stopped on drill ${mission.step} of ${mission.steps}`,
    };
  }

  return {
    label: 'Waves',
    value: view.won
      ? `all ${WAVE_COUNT} held`
      : `fell on wave ${waveOnScreen(mission.wave, mission.nextWaveAtTick !== undefined)} of ${WAVE_COUNT}`,
  };
}

/**
 * Metres, written the way somebody reads them.
 *
 * Kilometres above a thousand, because four digits of metres is a number nobody
 * parses at a glance and a sortie routinely drives several thousand. One decimal
 * place: the second one is noise on a figure this size.
 */
export function formatMetres(metres: number): string {
  const m = Number.isFinite(metres) ? Math.max(0, Math.round(metres)) : 0;
  if (m < 1000) return `${m} m`;
  return `${(m / 1000).toFixed(1)} km`;
}

/**
 * Where the objective marker goes on screen, given the bearing to it.
 *
 * Pure, and separated from `ArenaScene` for the reason `radar-geometry.ts` is
 * separated from `RadarScope`: this is the half that can be wrong in a way a
 * screenshot cannot show. An arrow pinned to the left edge and one pinned to the
 * right are a few pixels apart in a thumbnail and are opposite instructions, and
 * "reached" is one comparison that decides whether the HUD says *go there* or
 * *stop here*.
 *
 * **Bearing arithmetic rather than a projection**, which is the design decision
 * worth keeping. `Vector3.Project` has to drop anything behind the camera —
 * right for a nameplate, useless for an arrow, because "behind you" is exactly
 * when a player most needs telling which way to turn. An offset mapped through
 * the horizontal frustum has no such hole: every bearing answers.
 *
 * `offsetRad` is the shortest signed angle from the view direction to the
 * objective, so it is already wrapped; nothing here has to think about ±π.
 */
export function objectiveMarker(input: {
  /** Shortest signed angle from where the camera looks to where the objective is. */
  offsetRad: number;
  /** Horizontal half-angle of the frustum, radians. */
  halfFovRad: number;
  /** Viewport size in CSS pixels. */
  width: number;
  height: number;
  /** Metres from the tank to the objective. */
  distanceM: number;
  /** Radius of the marked ground, in metres. */
  radiusM: number;
}): { x: number; y: number; distanceM: number; clamped: boolean; side: -1 | 0 | 1; reached: boolean } {
  const { offsetRad, halfFovRad, width, height, distanceM, radiusM } = input;
  // Just inside the frustum, so the glyph stays whole rather than half off the
  // edge — the same 0.9 the gun marker pins at, and for the same reason.
  const limit = halfFovRad * 0.9;
  const clamped = Math.abs(offsetRad) >= limit;
  const bounded = Math.max(-limit, Math.min(limit, offsetRad));
  const focal = width / 2 / Math.tan(halfFovRad);
  return {
    x: width / 2 + Math.tan(bounded) * focal,
    // Above the sight rather than on it: the sight's ring is the reload and is
    // the one thing at the centre of the screen that must not be crowded.
    y: height / 2 - height * 0.14,
    distanceM,
    clamped,
    side: clamped ? (offsetRad > 0 ? 1 : -1) : 0,
    reached: distanceM <= radiusM,
  };
}

/**
 * The spawn-zone grace, in milliseconds — the same window the server keeps as
 * `SPAWN_ZONE_GRACE_TICKS`, restated here in the client's own unit.
 *
 * Derived from the shared constant rather than typed again: the number on
 * screen is a promise about when health starts falling, and a second copy is a
 * second thing to get wrong.
 */
export const SPAWN_ZONE_GRACE_MS = SPAWN_ZONE_GRACE_SECONDS * 1000;

/**
 * The enemy-spawn banner's countdown: how long until the damage starts, and
 * then that it has.
 *
 * **This mirrors a server mechanic rather than decorating one.** `Arena` counts
 * `ArenaPlayer.spawnZoneTicks` up while a tank is inside an enemy base's
 * `keepOut` circle and down while it is outside, at one tick each way, and
 * takes health only at the ceiling. This runs the identical rule in seconds
 * against the tank's own rendered position, so the number on screen is the
 * number the server is keeping — off by at most the latency, never by a rule.
 *
 * That is what it did not used to be. It was a six-second banner with a
 * sixty-second no-nag cooldown laid over damage that began the instant a tank
 * crossed the line: the countdown measured how long the *banner* had left while
 * the health bar measured something else entirely, and a second visit inside
 * the minute got no banner at all. Reported, correctly, as damage that arrives
 * "too quick to react" with "no notification about it on the screen".
 *
 * So there is no cooldown any more, and the banner is up for exactly as long as
 * the tank is inside. `FrameRateMeter`'s shape otherwise — private state, a
 * `performance.now()` handed in by the caller rather than read here, and a
 * `reset()` for a scene that outlives one sortie.
 *
 * `inside` — whether the tank's own rendered position is, right now, inside the
 * enemy base's circle — is entirely the caller's business. This file knows
 * nothing about geometry, teams or bases, on purpose: the "who is the enemy"
 * resolution belongs where every other friend/foe question in this HUD is
 * answered, relative to the local player's own side, never to a hardcoded
 * `'red'`/`'blue'`.
 */
export class SpawnZoneWarningTimer {
  /** Grace spent, in ms, clamped to `[0, SPAWN_ZONE_GRACE_MS]`. */
  private spentMs = 0;
  private lastMs: number | null = null;
  private inside = false;

  /** Advances the timer by one frame. Call once a frame, alive or not. */
  step(inside: boolean, nowMs: number): void {
    // A frame delta rather than a fixed step, because this is called from the
    // render loop — but clamped, so a backgrounded tab returning after a minute
    // resolves to neither an instant kill nor an instant pardon. Nothing reads
    // this back into prediction or the camera; it only picks a number to draw.
    const dt = this.lastMs === null ? 0 : Math.min(250, Math.max(0, nowMs - this.lastMs));
    this.lastMs = nowMs;
    this.inside = inside;
    this.spentMs = Math.max(0, Math.min(SPAWN_ZONE_GRACE_MS, this.spentMs + (inside ? dt : -dt)));
  }

  /**
   * What the banner should say, or `null` when the tank is not in the zone.
   *
   * `secondsLeft` counts 6 down to 1 and then reads 0 for the whole time the
   * damage is running, which is when `damaging` is true. A countdown that
   * stopped at 1 and vanished would take the warning away at the exact moment
   * it started being true.
   */
  warning(): { secondsLeft: number; damaging: boolean } | null {
    if (!this.inside) return null;
    const leftMs = SPAWN_ZONE_GRACE_MS - this.spentMs;
    return { secondsLeft: Math.max(0, Math.ceil(leftMs / 1000)), damaging: leftMs <= 0 };
  }

  /** Forgets everything. A scene reused for a new sortie starts clean. */
  reset(): void {
    this.spentMs = 0;
    this.lastMs = null;
    this.inside = false;
  }
}
